import Cookies from 'js-cookie';
import jwt_decode, { JwtPayload } from 'jwt-decode';

type UserPayload = {
  email: string;
  name: string;
  user_id: string;
  given_name: string;
  family_name: string;
  sub: string;
  is_impersonating: string;
};
type Jwt = JwtPayload & UserPayload;

const cookieNameCVIdToken = 'CVIdToken';
const cookieNameCVAccessToken = 'CVAccessToken';

type Token = {
  /** Cookie token that was used */
  token:
    | typeof cookieNameCVIdToken
    | typeof cookieNameCVAccessToken
    | 'MISSING';
  /** Decoded jwt */
  jwt: Jwt;
  /** Is the token valid */
  isValidToken: boolean;
  /** Is the user logged in */
  isLoggedIn: boolean;
  /** user payload from jwt */
  user: UserPayload;

  cvIdToken?: string;
  cvAccessToken?: string;
};

const decodeCookie = (accessToken: string) => {
  try {
    const jwt = jwt_decode<Jwt>(accessToken);
    return jwt;
  } catch (error) {
    return {} as Jwt;
  }
};

export const getCvAccessToken = () => {
  const CVAccessToken = Cookies.get(cookieNameCVAccessToken) || '';
  if (CVAccessToken) {
    let token = '';
    try {
      token = JSON.parse(CVAccessToken)?.access_token || CVAccessToken;
    } catch (error) {
      token = CVAccessToken;
    }

    return token;
  }

  return '';
};

const accessToken = (): Token => {
  const CVIdToken = Cookies.get(cookieNameCVIdToken) || '';
  const CVAccessToken = getCvAccessToken();
  const hasIdTokenAndAccessToken = !!CVIdToken && !!CVAccessToken;
  const hasAccessTokenAndNoIdToken = !CVIdToken && !!CVAccessToken;

  if (hasIdTokenAndAccessToken || hasAccessTokenAndNoIdToken) {
    const jwt = hasIdTokenAndAccessToken
      ? decodeCookie(CVIdToken)
      : decodeCookie(CVAccessToken);
    const token = hasIdTokenAndAccessToken
      ? cookieNameCVIdToken
      : cookieNameCVAccessToken;

    return {
      token,
      jwt,
      isValidToken: !!jwt && Object.keys(jwt).length > 0,
      isLoggedIn: !!jwt?.user_id,
      user: {
        email: jwt?.email,
        name: jwt?.name,
        user_id: jwt?.user_id,
        given_name: jwt?.given_name,
        family_name: jwt?.family_name,
        sub: jwt?.sub,
        is_impersonating: jwt?.is_impersonating,
      },
      cvIdToken: CVIdToken,
      cvAccessToken: CVAccessToken,
    };
  }

  return {
    token: 'MISSING',
    jwt: {} as Jwt,
    isValidToken: false,
    isLoggedIn: false,
    user: {} as UserPayload,
    cvIdToken: CVIdToken,
    cvAccessToken: CVAccessToken,
  };
};

export default accessToken;
